import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBestTimeSuggestions } from '../../hooks/useCalendar';
import type { BestTimeSlot } from '../../types/calendar';
import PlatformBadge from '../common/PlatformBadge';

interface BestTimesSidebarProps {
  onTimeSelect?: (slot: BestTimeSlot) => void;
}

/**
 * BestTimesSidebar component
 * Displays AI-driven scheduling suggestions with platform-specific best times
 * Click on a time slot to select it for scheduling
 */
export default function BestTimesSidebar({ onTimeSelect }: BestTimesSidebarProps) {
  const { t } = useTranslation();
  const [selectedPlatform, setSelectedPlatform] = useState<string | undefined>(undefined);

  // Fetch best time suggestions
  const { data, isLoading, error } = useBestTimeSuggestions(
    selectedPlatform ? { platform: selectedPlatform, limit: 5 } : { limit: 10 }
  );

  // Available platforms for filtering
  const platforms = ['linkedin', 'instagram', 'facebook', 'tiktok', 'youtube', 'twitter'] as const;

  /**
   * Group suggestions by platform
   */
  const groupedSuggestions = (suggestions: BestTimeSlot[] = []): Record<string, BestTimeSlot[]> => {
    return suggestions.reduce((acc, slot) => {
      const platform = slot.platform;
      if (!acc[platform]) {
        acc[platform] = [];
      }
      acc[platform].push(slot);
      return acc;
    }, {} as Record<string, BestTimeSlot[]>);
  };

  /**
   * Format score as percentage
   */
  const formatScore = (score: number): string => {
    return `${Math.round(score)}%`;
  };

  /**
   * Get score color based on value
   */
  const getScoreColor = (score: number): string => {
    if (score >= 80) return 'text-green-600 dark:text-green-400';
    if (score >= 60) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-gray-500 dark:text-gray-400';
  };

  /**
   * Handle time slot click
   */
  const handleSlotClick = (slot: BestTimeSlot) => {
    onTimeSelect?.(slot);
  };

  const suggestions = data?.suggestions || [];
  const grouped = selectedPlatform
    ? { [selectedPlatform]: suggestions }
    : groupedSuggestions(suggestions);

  return (
    <div className="border-2 border-black dark:border-gray-600 rounded-lg p-6 shadow-brutal bg-white dark:bg-gray-800">
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <svg
          className="w-5 h-5 text-yellow-500"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
        <h2 className="text-lg font-bold dark:text-gray-100">
          {t('calendar.bestTimes.title', 'Best Times to Post')}
        </h2>
      </div>

      {/* Platform Filter */}
      <div className="mb-4">
        <select
          value={selectedPlatform || ''}
          onChange={(e) => setSelectedPlatform(e.target.value || undefined)}
          className="w-full px-3 py-2 rounded-lg border-2 border-black dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white"
        >
          <option value="">{t('calendar.bestTimes.allPlatforms', 'All Platforms')}</option>
          {platforms.map((platform) => (
            <option key={platform} value={platform}>
              {platform.charAt(0).toUpperCase() + platform.slice(1)}
            </option>
          ))}
        </select>
      </div>

      {/* Suggestions List */}
      <div className="max-h-[400px] overflow-y-auto space-y-4">
        {isLoading ? (
          // Loading skeleton
          <div className="space-y-3">
            {[...Array(4)].map((_, i) => (
              <div
                key={i}
                className="h-16 bg-gray-100 dark:bg-gray-700 rounded-lg animate-pulse"
              />
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-4">
            <p className="text-red-600 dark:text-red-400 text-sm">
              {t('calendar.bestTimes.error', 'Failed to load suggestions')}
            </p>
          </div>
        ) : suggestions.length === 0 ? (
          // Empty state
          <div className="text-center py-8">
            <div className="text-4xl mb-2">
              <svg
                className="w-12 h-12 mx-auto text-gray-400 dark:text-gray-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">
              {t('calendar.bestTimes.empty', 'No suggestions yet')}
            </p>
            <p className="text-gray-400 dark:text-gray-500 text-xs mt-1">
              {t('calendar.bestTimes.emptyHint', 'Keep posting to see personalized suggestions')}
            </p>
          </div>
        ) : (
          // Grouped suggestions by platform
          Object.entries(grouped).map(([platform, slots]) => (
            <div key={platform} className="space-y-2">
              {/* Platform header */}
              <div className="flex items-center gap-2">
                <PlatformBadge
                  platform={platform as 'linkedin' | 'instagram' | 'facebook' | 'tiktok' | 'youtube' | 'twitter'}
                  className="text-[10px] px-1.5 py-0.5"
                />
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {t('calendar.bestTimes.topTimes', 'Top times')}
                </span>
              </div>

              {/* Time slots */}
              <div className="space-y-1">
                {slots.map((slot, index) => (
                  <button
                    key={`${slot.platform}-${slot.dayOfWeek}-${slot.hourOfDay}-${index}`}
                    onClick={() => handleSlotClick(slot)}
                    className="w-full flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-black dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-all duration-200 text-left group"
                  >
                    {/* Day and time */}
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 group-hover:text-black dark:group-hover:text-white">
                        {slot.dayName}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {slot.timeDisplay}
                      </p>
                    </div>

                    {/* Score */}
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-bold ${getScoreColor(slot.score)}`}>
                        {formatScore(slot.score)}
                      </span>
                      {/* Confidence indicator */}
                      {slot.metadata?.confidence && (
                        <div
                          className="w-2 h-2 rounded-full"
                          style={{
                            backgroundColor:
                              slot.metadata.confidence >= 0.8
                                ? '#22c55e'
                                : slot.metadata.confidence >= 0.5
                                ? '#eab308'
                                : '#9ca3af',
                          }}
                          title={t('calendar.bestTimes.confidence', 'Confidence: {{value}}%', {
                            value: Math.round(slot.metadata.confidence * 100),
                          })}
                        />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Footer with metadata */}
      {data?.metadata && suggestions.length > 0 && (
        <div className="pt-4 mt-4 border-t border-gray-200 dark:border-gray-700">
          <p className="text-xs text-gray-400 dark:text-gray-500 text-center">
            {t('calendar.bestTimes.dataInfo', 'Based on {{count}} posts', {
              count: data.metadata.sampleSize,
            })}
          </p>
        </div>
      )}
    </div>
  );
}
